import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { useStockPolicy, useUpsertStockPolicy } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { useLocationLookup } from '../../lib/useLocationLookup';
import { UOM_OPTIONS } from '../../model/options';
import { ProductIdCell } from './ProductIdCell';

export interface StockPolicyTarget {
  productId: string;
  locationId: string;
}

export interface StockPolicyDialogProps {
  target: StockPolicyTarget | null;
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toNonNegativeNumberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isNaN(n) || n < 0 ? undefined : n;
}

/**
 * Get/upsert the stock policy for one (product, location) pair — shared across the Stock and
 * Alerts tabs, opened via a row action. Read-only (disabled fields + an info alert) when the
 * viewer only holds `inventory.view`.
 */
export function StockPolicyDialog({
  target,
  canManage,
  onClose,
  onResult,
}: StockPolicyDialogProps) {
  const { t } = useTranslation('inventory');
  const { getLocationName } = useLocationLookup();
  const query = useStockPolicy(target?.productId, target?.locationId, { enabled: target !== null });
  const save = useUpsertStockPolicy();

  const [safetyStockThreshold, setSafetyStockThreshold] = useState('');
  const [reorderPoint, setReorderPoint] = useState('');
  const [checkAvailabilityThreshold, setCheckAvailabilityThreshold] = useState('');
  const [backorderAllowed, setBackorderAllowed] = useState(false);
  const [uom, setUom] = useState('EA');

  useEffect(() => {
    const p = query.data;
    if (!p) return;
    setSafetyStockThreshold(p.safetyStockThreshold != null ? String(p.safetyStockThreshold) : '');
    setReorderPoint(p.reorderPoint != null ? String(p.reorderPoint) : '');
    setCheckAvailabilityThreshold(
      p.checkAvailabilityThreshold != null ? String(p.checkAvailabilityThreshold) : '',
    );
    setBackorderAllowed(p.backorderAllowed);
    setUom(p.uom ?? 'EA');
  }, [query.data]);

  const open = target !== null;
  const notConfigured = query.isSuccess && query.data?.configured === false;
  const disabled = !canManage;

  const handleClose = () => {
    onClose();
  };

  const submit = () => {
    if (!target) return;
    save.mutate(
      {
        productId: target.productId,
        locationId: target.locationId,
        body: {
          safetyStockThreshold: toNonNegativeNumberOrUndefined(safetyStockThreshold),
          reorderPoint: toNonNegativeNumberOrUndefined(reorderPoint),
          checkAvailabilityThreshold: toNonNegativeNumberOrUndefined(checkAvailabilityThreshold),
          backorderAllowed,
          uom,
        },
      },
      {
        onSuccess: () => {
          onResult('success', t('stockPolicyDialog.success'));
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('stockPolicyDialog.title')}</DialogTitle>
      <DialogContent>
        {target ? (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t('stockPolicyDialog.productLabel')}:
              </Typography>
              <ProductIdCell productId={target.productId} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('stockPolicyDialog.locationLabel')}: {getLocationName(target.locationId)}
            </Typography>
          </Stack>
        ) : null}

        {query.isLoading ? <LoadingState label={t('stockPolicyDialog.loading')} /> : null}
        {query.isError ? (
          <ErrorState
            title={t('stockPolicyDialog.loadError.title')}
            message={query.error?.message ?? t('common:error.unknown')}
            onRetry={() => void query.refetch()}
          />
        ) : null}

        {query.isSuccess ? (
          <Stack spacing={2.5}>
            {notConfigured ? (
              <Alert severity="info">{t('stockPolicyDialog.notConfigured')}</Alert>
            ) : null}
            {disabled ? (
              <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>
                {t('stockPolicyDialog.readOnlyNotice')}
              </Alert>
            ) : null}
            <TextField
              label={t('stockPolicyDialog.safetyStockThresholdLabel')}
              helperText={t('stockPolicyDialog.safetyStockThresholdHelper')}
              type="number"
              value={safetyStockThreshold}
              onChange={(e) => setSafetyStockThreshold(e.target.value)}
              disabled={disabled}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            />
            <TextField
              label={t('stockPolicyDialog.reorderPointLabel')}
              type="number"
              value={reorderPoint}
              onChange={(e) => setReorderPoint(e.target.value)}
              disabled={disabled}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            />
            <TextField
              label={t('stockPolicyDialog.checkAvailabilityThresholdLabel')}
              type="number"
              value={checkAvailabilityThreshold}
              onChange={(e) => setCheckAvailabilityThreshold(e.target.value)}
              disabled={disabled}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            />
            <TextField
              select
              label={t('stockPolicyDialog.uomLabel')}
              required
              value={uom}
              onChange={(e) => setUom(e.target.value)}
              disabled={disabled}
              fullWidth
            >
              {UOM_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {t(o.label)}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={backorderAllowed}
                  onChange={(e) => setBackorderAllowed(e.target.checked)}
                  disabled={disabled}
                />
              }
              label={t('stockPolicyDialog.backorderAllowedLabel')}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={save.isPending}>
          {t('common:action.cancel')}
        </Button>
        {canManage ? (
          <Button
            variant="contained"
            onClick={submit}
            disabled={save.isPending || !query.isSuccess || uom.trim() === ''}
            startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('common:action.save')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
